import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '../services/authService';
import api from '../services/api';
import Toast from '../components/Common/Toast';

const Profile = () => {
  const user = authService.getCurrentUser();
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    current_password: '',
    password: '',
    password_confirmation: '',
  });
  const [formErrors, setFormErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});
    setLoading(true);

    try {
      const response = await api.post('/change-password', formData);
      if (response.data.success) {
        setToast({
          isOpen: true,
          message: 'Contraseña actualizada exitosamente',
          type: 'success'
        });
        // Limpiar el formulario
        setFormData({
          current_password: '',
          password: '',
          password_confirmation: '',
        });
      }
    } catch (error) {
      console.error('Error al cambiar contraseña:', error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cambiar la contraseña',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="mb-4">
              <h1 className="text-3xl font-bold text-gray-800">Mi Perfil</h1>
              <p className="text-gray-600 mt-1">Gestiona tu información personal y seguridad</p>
            </div>

            {/* Breadcrumb */}
            <nav className="text-sm text-gray-600 mb-4">
              <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-800">Mi Perfil</span>
            </nav>
          </div>

          {/* Información del Usuario */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Información Personal</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <p className="text-gray-900">{user?.name || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <p className="text-gray-900">{user?.email || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                <span className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${
                  user?.role === 'administrador'
                    ? 'bg-purple-100 text-purple-800'
                    : user?.role === 'limitado'
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-blue-100 text-blue-800'
                }`}>
                  {user?.role === 'administrador'
                    ? 'Administrador'
                    : user?.role === 'limitado'
                      ? 'Limitado'
                      : 'Usuario'}
                </span>
              </div>
            </div>
          </div>

          {/* Cambiar Contraseña */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Cambiar Contraseña</h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contraseña Actual <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  name="current_password"
                  value={formData.current_password}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                    formErrors.current_password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {formErrors.current_password && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.current_password[0]}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nueva Contraseña <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                    formErrors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                  minLength={8}
                />
                {formErrors.password && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.password[0]}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">La contraseña debe tener al menos 8 caracteres</p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmar Nueva Contraseña <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  name="password_confirmation"
                  value={formData.password_confirmation}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                    formErrors.password_confirmation ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                  minLength={8}
                />
                {formErrors.password_confirmation && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.password_confirmation[0]}</p>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Actualizando...' : 'Actualizar Contraseña'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Toast de notificaciones */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default Profile;
